import { useQuery } from '@tanstack/react-query';
import { getAddress } from 'viem';

import { queryKeys } from '@/lib/query';

import {
    isTradingSessionWalletMissingError,
    resolveSessionAnchor,
} from './walletResolution';

export interface OnboardedWalletPresence {
    /** The reads finished, one way or the other. */
    resolved: boolean;
    /**
     * Both custodial wallets exist on chain for this owner. Null while unknown
     * (not attempted yet, or the RPC failed): callers must treat null as "no
     * answer", never as "no wallet".
     */
    onboarded: boolean | null;
}

const UNKNOWN: OnboardedWalletPresence = { resolved: false, onboarded: null };

/** Owner EOAs rarely gain or lose custodial wallets; one read per app load. */
const PRESENCE_STALE_MS = 5 * 60_000;

/**
 * Onboarding waits on this answer before it decides anything, so it must always
 * arrive. A public RPC that hangs settles as "unknown" instead.
 */
const READ_TIMEOUT_MS = 6_000;

async function withTimeout<T>(work: Promise<T>): Promise<T> {
    let timer: number | undefined;
    try {
        return await Promise.race([
            work,
            new Promise<never>((_resolve, reject) => {
                timer = window.setTimeout(
                    () => reject(new Error('Wallet presence read timed out.')),
                    READ_TIMEOUT_MS,
                );
            }),
        ]);
    } finally {
        if (timer !== undefined) window.clearTimeout(timer);
    }
}

/**
 * "Has this wallet finished onboarding?", answered from chain.
 *
 * The signal is `resolveSessionAnchor`: `wallets(owner)` non-zero on EITHER
 * anchor's authorization manager (Cronos or Robinhood) AND on the HyperEVM
 * trading manager. It is the cheapest reliable answer available:
 *
 * - It is exactly the precondition the trading-window ceremony itself enforces,
 *   so a `true` here can never produce a prompt that dead-ends, and a half
 *   provisioned user (anchor leg only) correctly reads as not onboarded.
 * - It is durable and device independent, unlike the local per-wallet
 *   onboarding record, which is missing on a second browser and on cleared
 *   storage — the exact users who would otherwise be dropped into demo mode.
 * - It needs no app token, no cookie and no signature, so it answers on a cold
 *   load before any authority exists.
 * - It costs three `eth_call`s (both anchors + HyperEVM), deduplicated and
 *   cached by React Query, so every consumer of this hook shares one read set.
 *
 * The backend speed balance row is deliberately NOT the signal: it is created
 * by the first deposit, not by onboarding, so it reports "no wallet" for every
 * wallet-first user who has not deposited yet.
 */
export function useOnboardedWalletPresence(
    address: string | undefined,
    enabled = true,
): OnboardedWalletPresence {
    const active = enabled && !!address;
    const query = useQuery({
        queryKey: queryKeys.tradingSession.walletPresence(
            address?.toLowerCase() ?? '',
        ),
        enabled: active,
        staleTime: PRESENCE_STALE_MS,
        retry: false,
        queryFn: async () => {
            try {
                await withTimeout(
                    resolveSessionAnchor(getAddress(address!)),
                );
                return true;
            } catch (error) {
                if (isTradingSessionWalletMissingError(error)) return false;
                throw error;
            }
        },
    });

    if (!active) return UNKNOWN;
    if (query.data !== undefined) {
        return { resolved: true, onboarded: query.data };
    }
    // an RPC that failed settles as "unknown": nothing may read a dead public
    // node as proof that the user never onboarded.
    if (query.isError) return { resolved: true, onboarded: null };
    return UNKNOWN;
}
